import { action, makeObservable, runInAction } from 'mobx';

import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import ConnectionStore from 'stores/connection/ConnectionStore';
import { SortDirection } from 'constants/SortDirection';
import { IDataFetchOptions } from '../../types';
import { getConditions, getListConditions } from 'stores/_services/filterService';
import { buildOptions, get, getCount, getPage } from 'stores/_services/dataService';
import { buildCsvDownloadUrl } from 'stores/_services/downloadService';
import getAnalysisContext from '../../helpers/getAnalysisContext';

const SEARCH_FIELDS = ['remoteAddrString'];

export default class ConnectionListStore extends BaseListStore<ConnectionStore> {
    constructor() {
        super({
            itemStore: ConnectionStore,
            sortBy: 'hitCount',
            sortDirection: SortDirection.DESC
        });

        makeObservable(this, {
            getConnections: action,
            getConnection: action
        });
    }

    async getConnections(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const { scanId, boxId, route } = await getAnalysisContext('connections');
        const conditions = getConditions(this.filter);
        conditions.push({ scanId }, { boxId });

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            }
        };

        const { data } = await getPage(route, options);
        const totalCount = await getCount(route, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getConnection(connectionId: string): Promise<void> {
        const { route } = await getAnalysisContext('connections');
        const { data } = await get(`${route}/${connectionId}`);

        this._updateData(data);
    }

    public async exportToCsv(): Promise<void> {
        const { scanId, boxId, route } = await getAnalysisContext('connections');
        const conditions = getListConditions(this.selectedIdList, this.filter);
        conditions.push({ scanId, boxId });

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            },
            excludeFields: ['id', 'boxId', 'remoteAddrString']
        };

        const query = buildOptions(options);

        window.location = (await buildCsvDownloadUrl(route, query)) as unknown as Location;
    }
}
